import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import WorkCard from "@/components/WorkCard";
import CharacterCard from "@/components/CharacterCard";
import DonateModal from "@/components/DonateModal";
import api from "@/lib/api";
import { Feather, ArrowRight, EyeOff, DollarSign, Heart, Swords } from "lucide-react";

export default function Landing() {
  const [works, setWorks] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [donateWork, setDonateWork] = useState(null);

  useEffect(() => {
    api.get("/works").then(({ data }) => setWorks(data.slice(0, 3))).catch(() => {});
    api.get("/characters").then(({ data }) => setCharacters(data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen grain">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20 sm:pb-28 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
          <div>
            <div className="chip mb-8" data-testid="hero-chip">
              <Feather className="w-3 h-3" /> An anonymous market of unfinished tales
            </div>
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#F7F3EC] mb-6">
              Sell what you{" "}<br/>
              <span className="italic text-[#E6B860]">never finished.</span>
            </h1>
            <p className="font-serif-body text-lg sm:text-xl text-[#A8A095] leading-relaxed max-w-xl mb-10">
              Anonscribe Archive is a discreet marketplace where authors offer drafts, fragments, abandoned chapters, and forgotten heroes & villains under a pen name — sold to readers who prefer their fiction unresolved.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/browse" className="btn-buy px-6 py-3.5 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2" data-testid="hero-browse">
                Browse the archive <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/auth?mode=register" className="btn-ghost-gold px-6 py-3.5 rounded-sm font-mono-brass text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="hero-sell">
                Sell anonymously
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-4 max-w-lg">
              {[
                { icon: EyeOff, label: "Pen name", sub: "Always" },
                { icon: DollarSign, label: "Your price", sub: "You set" },
                { icon: Heart, label: "Tip jar", sub: "Withdrawable" },
              ].map(({ icon: Icon, label, sub }) => (
                <div key={label} className="border-l border-[rgba(217,180,114,0.2)] pl-4">
                  <Icon className="w-4 h-4 text-[#D9B472] mb-2" />
                  <div className="text-[#F7F3EC] text-sm font-serif-display">{label}</div>
                  <div className="text-[#6E675E] text-xs font-mono-brass uppercase tracking-widest mt-0.5">{sub}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="paper-scroll gold-frame rounded-sm p-8 sm:p-10 relative">
              <div className="absolute top-4 right-4 chip" data-testid="hero-card-status">62% Draft</div>
              <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-3">Fragment · Novel</div>
              <h3 className="font-serif-display text-3xl text-[#F7F3EC] leading-tight mb-5">The Cartographer of Lost Rooms</h3>
              <p className="font-serif-body text-base leading-relaxed text-[#A8A095] italic mb-6">
                "The house had seventeen rooms the night I bought it, and by morning there were nineteen. I sat on the porch drinking coffee that had gone cold in my hands…"
              </p>
              <div className="hairline mb-5" />
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">Pseudonym</div>
                  <div className="text-[#D9B472] font-serif-display italic text-lg">The Anonymous Editor</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">Unlock</div>
                  <div className="text-[#F7F3EC] font-mono-brass text-lg">$7.99</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-[rgba(217,180,114,0.15)] rounded-sm -z-10 hidden sm:block" />
            <div className="absolute -top-4 -left-4 w-32 h-32 border border-[rgba(217,180,114,0.08)] rounded-sm -z-10 hidden sm:block" />
          </div>
        </div>
      </section>

      <div className="hairline max-w-7xl mx-auto" />

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="chip mb-3">Freshly abandoned</div>
            <h2 className="font-serif-display text-3xl sm:text-5xl text-[#F7F3EC] tracking-tight">Recent fragments</h2>
          </div>
          <Link to="/browse" className="hidden sm:inline-flex items-center gap-2 text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest hover:text-[#E6B860] transition-colors duration-200" data-testid="landing-view-all">
            View archive <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {works.length === 0 ? (
            <p className="text-[#6E675E] col-span-full font-serif-body">The archive stirs. Come back soon.</p>
          ) : (
            works.map((w, i) => <WorkCard key={w.id} work={w} onDonate={setDonateWork} index={i} />)
          )}
        </div>
      </section>

      {/* Character Vault */}
      <section className="border-t border-[rgba(217,180,114,0.1)] bg-gradient-to-b from-[#0D0C0A] to-[#111]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <div className="chip mb-3"><Swords className="w-3 h-3" /> The Character Vault</div>
              <h2 className="font-serif-display text-3xl sm:text-5xl text-[#F7F3EC] tracking-tight max-w-2xl">
                Forgotten heroes and villains, <span className="italic text-[#E6B860]">for hire.</span>
              </h2>
              <p className="font-serif-body text-lg text-[#A8A095] max-w-2xl mt-4 leading-relaxed">
                Adopt a character no one else will finish — with a stat block, a portrait, and <span className="text-[#D9B472] italic">infinite battles and romance</span> ahead. Some carry a roleplay licence so you can drop them into your own game or novel.
              </p>
            </div>
            <Link to="/vault" className="btn-buy px-5 py-3 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2 self-start sm:self-auto" data-testid="landing-vault-cta">
              Enter the Vault <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {characters.length === 0 ? (
            <p className="text-[#6E675E] font-serif-body italic">The Vault is quiet. The first heroes are being written.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {characters.map((c, i) => <CharacterCard key={c.id} character={c} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-t border-[rgba(217,180,114,0.1)] bg-[#111]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <h2 className="font-serif-display text-3xl sm:text-5xl text-[#F7F3EC] tracking-tight mb-14 max-w-2xl">A different kind of manuscript sale.</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "01", h: "Write under a name", b: "Register with a pen name of your choosing. Your legal identity is never exposed to buyers." },
              { n: "02", h: "Price the fragment", b: "Set your unlock price. Readers see a free excerpt before paying to open the manuscript vault." },
              { n: "03", h: "Take tips, withdraw", b: "Buyers can also send anonymous tips. Both sales and tips settle to your wallet — withdraw anytime." },
            ].map((s) => (
              <div key={s.n}>
                <div className="font-mono-brass text-[#D9B472] text-sm mb-4">{s.n}</div>
                <h3 className="font-serif-display text-2xl text-[#F7F3EC] mb-3">{s.h}</h3>
                <p className="font-serif-body text-[#A8A095] leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[rgba(217,180,114,0.1)] py-10 text-center text-xs font-mono-brass text-[#6E675E] uppercase tracking-widest">
        <Feather className="w-4 h-4 inline-block mr-2 text-[#D9B472]" />
        Anonscribe Archive · Anonymous fragments, sold discreetly
        <span className="mx-3 text-[#3a352e]">·</span>
        <Link to="/policy" className="hover:text-[#D9B472] transition-colors duration-200" data-testid="footer-policy">Content Policy</Link>
      </footer>

      {donateWork && <DonateModal work={donateWork} onClose={() => setDonateWork(null)} />}
    </div>
  );
}
